import {jsx as $hgUW1$jsx} from "react/jsx-runtime";
import {createContext as $hgUW1$createContext, useContext as $hgUW1$useContext, useRef as $hgUW1$useRef, useState as $hgUW1$useState, useEffect as $hgUW1$useEffect, useLayoutEffect as $hgUW1$useLayoutEffect, useMemo as $hgUW1$useMemo} from "react";
import $hgUW1$nextimage from "next/image";
import $hgUW1$sanityimageurl from "@sanity/image-url";



const $fb7efaf9bd9b403e$var$SanityProjectDetailsContext = /*#__PURE__*/ (0, $hgUW1$createContext)(null);
function $fb7efaf9bd9b403e$export$8e79174a00298caa() {
    const projectDetails = (0, $hgUW1$useContext)($fb7efaf9bd9b403e$var$SanityProjectDetailsContext);
    if (!projectDetails) throw new Error("useProjectDetails must be used within ClientProvider");
    return projectDetails;
}
function $fb7efaf9bd9b403e$export$2e2bcd8739ae039({ children: children, projectDetails: projectDetails }) {
    return /*#__PURE__*/ (0, $hgUW1$jsx)($fb7efaf9bd9b403e$var$SanityProjectDetailsContext.Provider, {
        value: projectDetails,
        children: children
    });
}






function $73bc8d6fb90f9f78$var$useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = (0, $hgUW1$useState)(value);
    (0, $hgUW1$useEffect)(()=>{
        const timer = setTimeout(()=>setDebouncedValue(value), delay || 500);
        return ()=>{
            clearTimeout(timer);
        };
    }, [
        value,
        delay
    ]);
    return debouncedValue;
}
var $73bc8d6fb90f9f78$export$2e2bcd8739ae039 = $73bc8d6fb90f9f78$var$useDebounce;



function $7ab7fb1c8d3c5ed0$export$2e2bcd8739ae039(ref) {
    const [dimensions, setDimensions] = (0, $hgUW1$useState)(null);
    (0, $hgUW1$useLayoutEffect)(()=>{
        if (ref.current) {
            const element = ref.current;
            setDimensions({
                width: element.offsetWidth,
                height: element.offsetHeight
            });
            const observer = new ResizeObserver((entries)=>{
                const rect = entries[0].contentRect;
                setDimensions({
                    width: rect.width,
                    height: rect.height
                });
            });
            observer.observe(element);
            return ()=>{
                observer.unobserve(element);
            };
        }
    }, [
        ref
    ]);
    return dimensions;
}




const $69ef802740cf2a59$export$3d3f27418a1a2d21 = (projectDetails, image, aspectRatio)=>{
    // First find the natural width and height from the asset ID. This
    // seems a bit hacky, but it's the same thing next-sanity-image
    // does, so it's likely to be the correct way of doing it. If we
    // couldn't parse the size form the ID we would have to do another
    // request to fetch the asset document, which would be annoying.
    const dimensions = image.asset._ref.split("-")[2];
    let [width, height] = dimensions.split("x").map((num)=>parseInt(num, 10));
    // If the image has been cropped, adjust the width and height to
    // the cropped size:
    const crop = image.crop;
    if (crop) {
        width = Math.round(width * (1 - (crop.left + crop.right)));
        height = Math.round(height * (1 - (crop.top + crop.bottom)));
    }
    // If the user asked for a different aspect ratio, shrink either
    // the height or width depending on whether the image should be
    // wider or more narrow than it naturally is:
    if (aspectRatio) {
        const naturalAspectRatio = width / height;
        if (aspectRatio > naturalAspectRatio) height = Math.round(width / aspectRatio);
        else width = Math.round(height * aspectRatio);
    }
    const src = (0, $hgUW1$sanityimageurl)(projectDetails).image(image).width(width).height(height).url();
    return {
        src: src,
        width: width,
        height: height
    };
};


function $a27a2f5ed6aac119$export$2e2bcd8739ae039(projectDetails, image, aspectRatio) {
    const sanityImage = (0, $hgUW1$useMemo)(()=>(0, $69ef802740cf2a59$export$3d3f27418a1a2d21)(projectDetails, image, aspectRatio), [
        projectDetails,
        image,
        aspectRatio
    ]);
    return sanityImage;
}



function $99a140ad618ced19$export$2e2bcd8739ae039({ image: image, aspectRatio: aspectRatio, style: style = {}, ...rest }) {
    const projectDetails = (0, $fb7efaf9bd9b403e$export$8e79174a00298caa)();
    const { src: src, width: width, height: height } = (0, $a27a2f5ed6aac119$export$2e2bcd8739ae039)(projectDetails, image, aspectRatio);
    const ref = (0, $hgUW1$useRef)(null);
    const dimensions = (0, $7ab7fb1c8d3c5ed0$export$2e2bcd8739ae039)(ref);
    const debouncedDimensions = (0, $73bc8d6fb90f9f78$export$2e2bcd8739ae039)(dimensions, 1000);
    // For the first render the measured dimensions will be null as the image
    // element hasn't been mounted yet. This means we won't have a reasonable
    // size to set in the size attribute, causing next.Image to render a blurry
    // 64px version of the image.
    //
    // To get out of that state as quickly as possible, we don't want to
    // debounce the change from null dimensions to actual dimensions. We do
    // that by using the raw measured dimensions until the debounced dimensions
    // change into _something_.
    //
    // If we didn't do this, we would (and did) show a blurry image for a
    // second while we wait for the debounce timer.
    const sizes = debouncedDimensions?.width ?? dimensions?.width;
    return /* eslint-disable jsx-a11y/alt-text */ /*#__PURE__*/ (0, $hgUW1$jsx)((0, $hgUW1$nextimage), {
        ref: ref,
        src: src,
        width: width,
        height: height,
        sizes: `${sizes ?? 0}px`,
        style: {
            display: "block",
            width: "100%",
            height: "auto",
            aspectRatio: width / height,
            ...style ?? {}
        },
        ...rest
    });
}









function $5e6e23fe1ab7858d$export$2e2bcd8739ae039({ image: image, aspectRatio: aspectRatio, style: style = {}, ...rest }) {
    const projectDetails = (0, $fb7efaf9bd9b403e$export$8e79174a00298caa)();
    const { src: src, width: width, height: height } = (0, $a27a2f5ed6aac119$export$2e2bcd8739ae039)(projectDetails, image, aspectRatio);
    const ref = (0, $hgUW1$useRef)(null);
    const dimensions = (0, $7ab7fb1c8d3c5ed0$export$2e2bcd8739ae039)(ref);
    const debouncedDimensions = (0, $73bc8d6fb90f9f78$export$2e2bcd8739ae039)(dimensions, 1000);
    // NOTE: This code tries to get the browser to move the image within the
    //       filled rectangle by manipulating the object position.
    //
    //       Ideally this should move the hotspot as close to the center of
    //       the visible rectangle but it ends up being a little off.
    //
    //       The behaviour also seem to differ between browsers, so we will
    //       have to re-visit this. It's still better than just cropping to
    //       the center though, so we can keep it until we have a better
    //       solution.
    const objectPosition = (0, $hgUW1$useMemo)(()=>{
        let objectPosition = {
            x: 0.5,
            y: 0.5
        };
        if (image.hotspot) {
            if (image.crop) objectPosition = {
                x: (image.hotspot.x - image.crop.left) / (1 - image.crop.left - image.crop.right),
                y: (image.hotspot.y - image.crop.top) / (1 - image.crop.top - image.crop.bottom)
            };
            else {
                objectPosition.x = image.hotspot.x;
                objectPosition.y = image.hotspot.y;
            }
        }
        return objectPosition;
    }, [
        image
    ]);
    const imageAspect = width / height;
    // For the first render the measured dimensions will be null as the image
    // element hasn't been mounted yet. This means we won't have a reasonable
    // size to set in the size attribute, causing next.Image to render a blurry
    // 64px version of the image.
    //
    // To get out of that state as quickly as possible, we don't want to
    // debounce the change from null dimensions to actual dimensions. We do
    // that by using the raw measured dimensions until the debounced dimensions
    // change into _something_.
    //
    // If we didn't do this, we would (and did) show a blurry image for a
    // second while we wait for the debounce timer.
    const currentDimensions = debouncedDimensions ?? dimensions;
    let sizes = 0;
    if (currentDimensions) {
        sizes = currentDimensions.width;
        // If the image is wider than the frame it is cropped into, adjust the
        // sizes prop so that it represents the real width of the image,
        // including the parts hidden outside the frame.
        //
        // This ensures we load a variant of the image with enough pixels to
        // show the visible part in full detail.
        const measuredAspect = currentDimensions.width / currentDimensions.height;
        if (imageAspect > measuredAspect) sizes = imageAspect * currentDimensions.height;
    }
    return /* eslint-disable jsx-a11y/alt-text */ /*#__PURE__*/ (0, $hgUW1$jsx)((0, $hgUW1$nextimage), {
        ref: ref,
        src: src,
        sizes: `${sizes}px`,
        fill: true,
        style: {
            objectFit: "cover",
            objectPosition: `left ${objectPosition.x * 100}% top ${objectPosition.y * 100}%`,
            ...style
        },
        ...rest
    });
}









function $3567f9ab1c6e0a7a$export$2e2bcd8739ae039({ image: image, aspectRatio: aspectRatio, style: style = {}, ...rest }) {
    const projectDetails = (0, $fb7efaf9bd9b403e$export$8e79174a00298caa)();
    const { src: src, width: width, height: height } = (0, $a27a2f5ed6aac119$export$2e2bcd8739ae039)(projectDetails, image, aspectRatio);
    const ref = (0, $hgUW1$useRef)(null);
    const dimensions = (0, $7ab7fb1c8d3c5ed0$export$2e2bcd8739ae039)(ref);
    const debouncedDimensions = (0, $73bc8d6fb90f9f78$export$2e2bcd8739ae039)(dimensions, 1000);
    // For the first render the measured dimensions will be null as the image
    // element hasn't been mounted yet. This means we won't have a reasonable
    // size to set in the size attribute, causing next.Image to render a blurry
    // 64px version of the image.
    //
    // To get out of that state as quickly as possible, we don't want to
    // debounce the change from null dimensions to actual dimensions. We do
    // that by using the raw measured dimensions until the debounced dimensions
    // change into _something_.
    //
    // If we didn't do this, we would (and did) show a blurry image for a
    // second while we wait for the debounce timer.
    const sizes = debouncedDimensions?.width ?? dimensions?.width ?? 0;
    return /* eslint-disable jsx-a11y/alt-text */ /*#__PURE__*/ (0, $hgUW1$jsx)((0, $hgUW1$nextimage), {
        ref: ref,
        src: src,
        width: width,
        height: height,
        sizes: `${sizes}px`,
        style: {
            aspectRatio: width / height,
            ...style
        },
        ...rest
    });
}







export {$fb7efaf9bd9b403e$export$2e2bcd8739ae039 as SanityProjectDetailsProvider, $fb7efaf9bd9b403e$export$8e79174a00298caa as useSanityProjectDetails, $99a140ad618ced19$export$2e2bcd8739ae039 as NextImageBlock, $5e6e23fe1ab7858d$export$2e2bcd8739ae039 as NextImageFill, $3567f9ab1c6e0a7a$export$2e2bcd8739ae039 as NextImageInline, $7ab7fb1c8d3c5ed0$export$2e2bcd8739ae039 as useDimensions, $a27a2f5ed6aac119$export$2e2bcd8739ae039 as useSanityImage, $69ef802740cf2a59$export$3d3f27418a1a2d21 as getSanityImage};
//# sourceMappingURL=module.js.map
